<script setup>
/**
 * Native HTML5 Drag & Drop Kanban
 * - No external deps
 * - Emits: moved({ taskId, toStatus, toIndex }), view(id), edit(id), delete(id), add(columnKey)
 */
const props = defineProps({
  columns: { type: Array, required: true }, // [{ key, title, dotBg, dot, items: [{ id, title, ...}] }]
  loading: { type: Boolean, default: false },
});
const emit = defineEmits(["moved", "view", "edit", "delete", "add"]);

function priorityClass(p) {
  return (
    {
      High: "badge bg-danger",
      Medium: "badge bg-warning",
      Low: "badge bg-success",
    }[p] || "badge bg-secondary"
  );
}
function progressClass(p) {
  if (p >= 100) return "bg-success";
  if (p >= 60) return "bg-purple";
  if (p >= 35) return "bg-warning";
  return "bg-danger";
}

/** drag state helpers */
function onDragStartCard(e, card) {
  // Put the task id in the dataTransfer
  e.dataTransfer.setData("text/plain", String(card.id));
  // Allow moving
  e.dataTransfer.effectAllowed = "move";
  // Optional ghost opacity
  e.target.classList.add("dragging");
}
function onDragEndCard(e) {
  e.target.classList.remove("dragging");
}

/** allow drop on the list container */
function onDragOverList(e) {
  e.preventDefault(); // required to allow drop
  e.dataTransfer.dropEffect = "move";
  const list = e.currentTarget;
  list.classList.add("dropping");
  // Optional: place a visual indicator based on pointer position
}
function onDragLeaveList(e) {
  const list = e.currentTarget;
  list.classList.remove("dropping");
}

/**
 * Compute index to insert based on where the pointer is
 * by comparing to existing card centers.
 */
function computeToIndex(listEl, clientY) {
  const cards = Array.from(listEl.querySelectorAll(".kanban-card")).filter(
    (c) => !c.classList.contains("dragging")
  );
  for (let i = 0; i < cards.length; i++) {
    const rect = cards[i].getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    if (clientY < mid) return i;
  }
  return cards.length; // append at end
}

function onDropList(e, toStatus) {
  e.preventDefault();
  const list = e.currentTarget;
  list.classList.remove("dropping");

  const idStr = e.dataTransfer.getData("text/plain");
  const taskId = Number(idStr || 0);
  if (!taskId) return;

  const toIndex = computeToIndex(list, e.clientY);
  emit("moved", { taskId, toStatus, toIndex });
}
</script>

<template>
  <div class="d-flex align-items-start overflow-auto project-status pb-4">
    <div
      v-for="col in props.columns"
      :key="col.key"
      class="p-3 rounded bg-transparent-secondary w-100 me-3"
    >
      <!-- Column header -->
      <div class="bg-white p-2 rounded mb-2">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <span class="p-1 d-flex rounded-circle me-2" :class="col.dotBg">
              <span class="rounded-circle d-block p-1" :class="col.dot"></span>
            </span>
            <h5 class="me-2 mb-0">{{ col.title }}</h5>
            <span class="badge bg-light rounded-pill">{{
              (col.items || []).length
            }}</span>
          </div>
          <!-- <div class="dropdown">
            <a href="javascript:void(0);" class="d-inline-flex align-items-center" data-bs-toggle="dropdown">
              <i class="ti ti-dots-vertical"></i>
            </a>
            <ul class="dropdown-menu dropdown-menu-end p-3">
              <li>
                <a href="javascript:void(0);" class="dropdown-item rounded-1">
                  <i class="ti ti-edit me-2"></i>Edit
                </a>
              </li>
              <li>
                <a href="javascript:void(0);" class="dropdown-item rounded-1">
                  <i class="ti ti-trash me-2"></i>Delete
                </a>
              </li>
            </ul>
          </div> -->
        </div>
      </div>

      <!-- Cards list (drop zone) -->
      <div
        class="kanban-list"
        @dragover="onDragOverList"
        @dragleave="onDragLeaveList"
        @drop="(e) => onDropList(e, col.key)"
      >
        <div
          v-for="card in col.items || []"
          :key="card.id"
          class="card kanban-card mb-2"
          draggable="true"
          @dragstart="(e) => onDragStartCard(e, card)"
          @dragend="onDragEndCard"
          :data-id="card.id"
          aria-grabbed="false"
        >
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div class="d-flex align-items-center">
                <span
                  v-if="card?.typeLabel"
                  class="badge bg-outline-dark me-2"
                  >{{ card.typeLabel }}</span
                >
                <span
                  class="badge-xs d-flex align-items-center justify-content-center"
                  :class="priorityClass(card?.priority)"
                >
                  <i class="fas fa-circle fs-6 me-1"></i
                  >{{ card?.priority || "—" }}
                </span>
              </div>
              <div class="dropdown">
                <a
                  href="javascript:void(0);"
                  class="d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i class="ti ti-dots-vertical"></i>
                </a>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click.prevent="emit('view', card.id)"
                      v-can="'Task.Read'"
                    >
                      <i class="ti ti-eye me-2"></i>View
                    </a>
                  </li>
                  <li>
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click.prevent="emit('edit', card.id)"
                      v-can="'Task.Edit'"
                    >
                      <i class="ti ti-edit me-2"></i>Edit
                    </a>
                  </li>
                  <li>
                    <a
                      href="javascript:void(0);"
                      class="dropdown-item rounded-1"
                      @click.prevent="emit('delete', card.id)"
                      v-can="'Task.Delete'"
                    >
                      <i class="ti ti-trash me-2"></i>Delete
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div class="mb-2">
              <h6
                class="d-flex align-items-center mb-0 text-truncate pointer"
                :title="card?.title"
                @click="emit('view', card.id)"
                v-can="'Task.Read'"
              >
                {{ card?.title || "—" }}
              </h6>
            </div>

            <div
              class="d-flex align-items-center mb-2"
              v-if="card?.progress !== undefined"
            >
              <div
                class="progress progress-sm flex-fill"
                role="progressbar"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div
                  class="progress-bar"
                  :class="progressClass(card.progress)"
                  :style="{ width: (card.progress || 0) + '%' }"
                ></div>
              </div>
              <span class="d-block ms-2 text-gray-9 fw-medium">{{
                (card.progress || 0) + "%"
              }}</span>
            </div>

            <p class="fw-medium mb-0" v-if="card?.due_pretty">
              Due on : <span class="text-gray-9">{{ card.due_pretty }}</span>
            </p>

            <div
              class="d-flex align-items-center justify-content-between border-top pt-2 mt-2"
            >
              <div class="avatar-list-stacked avatar-group-sm me-3">
                <span
                  v-for="(av, i) in (card?.assignee_avatars || []).slice(0, 5)"
                  :key="i"
                  class="avatar avatar-rounded"
                >
                  <img
                    class="border border-white"
                    :src="av || require('@/assets/img/profiles/avatar-02.jpg')"
                    alt="assignee"
                  />
                </span>
                <span
                  v-if="(card?.assignee_avatars || []).length > 5"
                  class="avatar avatar-rounded bg-primary fs-12"
                >
                  {{ card.assignee_avatars.length - 5 }}+
                </span>
              </div>
              <div class="d-flex align-items-center">
                <a
                  href="javascript:void(0);"
                  class="d-flex align-items-center text-dark me-3"
                >
                  <i class="ti ti-message-circle text-gray me-1"></i>
                  <span v-if="card?.comments != null">{{ card.comments }}</span>
                </a>
                <a
                  href="javascript:void(0);"
                  class="d-flex align-items-center text-dark"
                >
                  <i class="ti ti-paperclip text-gray me-1"></i>
                  <span v-if="card?.attachments != null">{{
                    card.attachments
                  }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state acts as drop zone too -->
        <div v-if="!(col.items && col.items.length)" class="empty-drop-zone">
          <span>Drop here</span>
        </div>
      </div>

      <div class="pt-2">
        <a
          v-can="'Task.Create'"
          href="javascript:void(0);"
          class="btn btn-white border border-dashed d-flex align-items-center justify-content-center"
          @click="emit('add', col.key)"
        >
          <i class="ti ti-plus me-2"></i> New Task
        </a>
      </div>
    </div>
  </div>

  <div v-if="props.loading" class="w-100 text-center py-3">
    <span class="spinner-border spinner-border-sm me-2"></span>Loading…
  </div>
</template>

<style scoped>
.pointer {
  cursor: pointer;
}
.kanban-list {
  min-height: 140px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 0.5rem;
  padding: 0.5rem;
  transition: background-color 0.15s ease;
}
.kanban-list.dropping {
  background: rgba(0, 123, 255, 0.08);
  outline: 2px dashed rgba(0, 123, 255, 0.4);
  outline-offset: -4px;
}
.kanban-card.dragging {
  opacity: 0.6;
}
.empty-drop-zone {
  border: 2px dashed rgba(0, 0, 0, 0.08);
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  color: #888;
}
</style>
